<template>
  <div class="workspace-picker" data-testid="workspace-picker">
    <header class="workspace-picker__header">
      <h1 class="workspace-picker__title">{{ copy.title }}</h1>
      <p class="workspace-picker__subtitle">{{ subtitle }}</p>
    </header>

    <AuthLoginAlert :message="alertMessage" />

    <div
      class="workspace-picker__identity-strip"
      data-testid="workspace-picker__identity-strip"
    >
      <img
        src="/img/auth/MailLoginIcon.svg"
        alt=""
        aria-hidden="true"
        class="workspace-picker__mail-icon"
      >
      <span class="workspace-picker__email">{{ displayEmail }}</span>
      <button
        type="button"
        class="workspace-picker__change"
        data-testid="workspace-picker__change"
        @click="$emit('back')"
      >
        {{ copy.change }}
      </button>
    </div>

    <ul class="workspace-picker__list" data-testid="workspace-picker__list">
      <li v-for="(tenant, index) in tenants" :key="tenantKey(tenant, index)">
        <button
          type="button"
          class="workspace-picker__row"
          :data-testid="`workspace-picker__row--${index}`"
          @click="onSelect(tenant)"
        >
          <span
            class="workspace-picker__avatar"
            :class="`workspace-picker__avatar--tone-${index % 3}`"
            aria-hidden="true"
          >
            {{ initialsFor(tenant) }}
          </span>
          <span class="workspace-picker__body">
            <span class="workspace-picker__title-row">
              <span class="workspace-picker__name">{{ nameFor(tenant) }}</span>
              <span
                v-if="isLastUsedTenant(tenant)"
                class="workspace-picker__last-used"
              >
                {{ copy.lastUsed }}
              </span>
            </span>
            <span v-if="tenant.hostname" class="workspace-picker__hostname">
              {{ tenant.hostname }}
            </span>
            <span v-if="metaFor(tenant)" class="workspace-picker__meta">
              {{ metaFor(tenant) }}
            </span>
          </span>
          <v-icon class="workspace-picker__chevron" size="16">mdi-chevron-right</v-icon>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import constants from '../../../plugins/constants';
import AuthLoginAlert from './AuthLoginAlert.vue';
import {
  initialsFromDisplayName,
  isLastUsedTenant,
  tenantMetaLine,
  workspaceDisplayName,
} from './WorkspacePickerUtils';

const { AUTH } = constants;

export default {
  name: 'WorkspacePicker',
  components: {
    AuthLoginAlert,
  },
  props: {
    tenants: { type: Array, default: () => [] },
    email: { type: String, default: '' },
    alertMessage: { type: String, default: '' },
  },
  data() {
    return {
      copy: {
        title: AUTH.CHOOSE_WORKSPACE_TITLE,
        change: AUTH.CHOOSE_WORKSPACE_CHANGE,
        lastUsed: AUTH.CHOOSE_WORKSPACE_LAST_USED,
        fallbackName: AUTH.CHOOSE_WORKSPACE_FALLBACK_NAME,
      },
    };
  },
  computed: {
    ...mapState('auth', ['discoverEmail']),
    displayEmail() {
      return this.email || this.discoverEmail || '';
    },
    workspaceCount() {
      return Array.isArray(this.tenants) ? this.tenants.length : 0;
    },
    subtitle() {
      return String(AUTH.CHOOSE_WORKSPACE_ACCESS || '').replace(
        '{count}',
        String(this.workspaceCount),
      );
    },
  },
  beforeDestroy() {
    this.copy = {
      title: AUTH.CHOOSE_WORKSPACE_TITLE,
      change: AUTH.CHOOSE_WORKSPACE_CHANGE,
      lastUsed: AUTH.CHOOSE_WORKSPACE_LAST_USED,
      fallbackName: AUTH.CHOOSE_WORKSPACE_FALLBACK_NAME,
    };
  },
  methods: {
    isLastUsedTenant,
    tenantKey(tenant, index) {
      return tenant?.hostname || tenant?.client || `workspace-${index}`;
    },
    nameFor(tenant) {
      return workspaceDisplayName(tenant, this.copy.fallbackName);
    },
    initialsFor(tenant) {
      return initialsFromDisplayName(this.nameFor(tenant));
    },
    metaFor(tenant) {
      return tenantMetaLine(tenant);
    },
    onSelect(tenant) {
      this.$emit('select', tenant);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './WorkspacePicker.scss';
</style>
